/**
 * 第三幕考验卡牌定义 — 20张
 *
 * 进入第三幕时随机抽取2张，游戏过程中按条件触发第3张隐藏考验。
 */
import type { TrialCard } from '../types/cards';

// ============================================
// 距离考验（3张）
// ============================================
const DISTANCE_TRIALS: TrialCard[] = [
  {
    id: 'trial_dist_01',
    name: '工作调动',
    category: '距离',
    description: '一方接到外地工作调动通知，需要异地一段时间',
    hint: '理性沟通是最稳妥的选择',
    duration: 2,
    autoDamage: { commitment: -5, energy: -10, stability: -3 },
    strategyRewards: {
      '全力维系': { commitmentChange: 8, energyCost: 30, stabilityChange: 3 },
      '理性沟通': { commitmentChange: 8, energyCost: 15, stabilityChange: 5 },
      '暂时退让': { commitmentChange: -3, energyCost: 5, stabilityChange: 1 },
      '冷处理': { commitmentChange: -8, energyCost: -15, stabilityChange: -2, risk: '对方觉得你不在乎' },
    },
  },
  {
    id: 'trial_dist_02',
    name: '长期出差',
    category: '距离',
    description: '一方需要长期出差，每周只能见一次面',
    hint: '保持联系很重要',
    duration: 2,
    autoDamage: { commitment: -3, energy: -8, stability: -2 },
    strategyRewards: {
      '全力维系': { commitmentChange: 5, energyCost: 30, stabilityChange: 3 },
      '理性沟通': { commitmentChange: 5, energyCost: 15, stabilityChange: 3 },
      '暂时退让': { commitmentChange: -2, energyCost: 5, stabilityChange: 1 },
      '冷处理': { commitmentChange: -5, energyCost: -15, stabilityChange: -1 },
    },
  },
  {
    id: 'trial_dist_03',
    name: '异地恋情',
    category: '距离',
    description: '因为工作原因不得不开始异地恋，距离和时差都是考验',
    hint: '高难度考验，需要大量精力投入',
    duration: 3,
    autoDamage: { commitment: -8, energy: -15, stability: -5 },
    strategyRewards: {
      '全力维系': { commitmentChange: 10, energyCost: 30, stabilityChange: 5 },
      '理性沟通': { commitmentChange: 10, energyCost: 15, stabilityChange: 8 },
      '暂时退让': { commitmentChange: -5, energyCost: 5, stabilityChange: 0 },
      '冷处理': { commitmentChange: -10, energyCost: -15, stabilityChange: -5, risk: '被误解为不在乎' },
      '惊喜破局': { commitmentChange: 15, energyCost: 25, stabilityChange: 8 },
    },
  },
];

// ============================================
// 经济考验（3张）
// ============================================
const ECONOMY_TRIALS: TrialCard[] = [
  {
    id: 'trial_econ_01',
    name: '收入差距',
    category: '经济',
    description: '两人收入差距逐渐显现，消费观念开始产生摩擦',
    hint: '全力维系能证明不离不弃',
    duration: 2,
    autoDamage: { commitment: -4, energy: -5, stability: -5 },
    strategyRewards: {
      '全力维系': { commitmentChange: 10, energyCost: 30, stabilityChange: 3 },
      '理性沟通': { commitmentChange: 5, energyCost: 15, stabilityChange: 8 },
      '暂时退让': { commitmentChange: -2, energyCost: 5, stabilityChange: 2 },
      '冷处理': { commitmentChange: -5, energyCost: -15, stabilityChange: -3 },
    },
  },
  {
    id: 'trial_econ_02',
    name: '失业危机',
    category: '经济',
    description: '一方突然失业，经济压力骤增',
    hint: '这是证明真心的时刻',
    duration: 2,
    autoDamage: { commitment: -6, energy: -12, stability: -8 },
    strategyRewards: {
      '全力维系': { commitmentChange: 15, energyCost: 30, stabilityChange: 5, risk: '精力消耗巨大' },
      '理性沟通': { commitmentChange: 8, energyCost: 15, stabilityChange: 5 },
      '暂时退让': { commitmentChange: -3, energyCost: 5, stabilityChange: 0 },
      '冷处理': { commitmentChange: -10, energyCost: -15, stabilityChange: -5, risk: '对方觉得你嫌弃' },
    },
  },
  {
    id: 'trial_econ_03',
    name: '消费冲突',
    category: '经济',
    description: '在一笔大额消费上产生严重分歧',
    hint: '建立共识比赢得争论更重要',
    duration: 1,
    autoDamage: { commitment: -3, energy: -5, stability: -8 },
    strategyRewards: {
      '全力维系': { commitmentChange: 5, energyCost: 30, stabilityChange: 5 },
      '理性沟通': { commitmentChange: 5, energyCost: 15, stabilityChange: 10 },
      '暂时退让': { commitmentChange: -1, energyCost: 5, stabilityChange: 3 },
      '冷处理': { commitmentChange: -3, energyCost: -15, stabilityChange: -2 },
    },
  },
];

// ============================================
// 信任考验（4张）
// ============================================
const TRUST_TRIALS: TrialCard[] = [
  {
    id: 'trial_trst_01',
    name: '前任联系',
    category: '信任',
    description: '发现对方和前任还有联系，信任面临考验',
    hint: '深度坦白是高风险高回报的选择',
    duration: 1,
    autoDamage: { commitment: -10, energy: -8, stability: -15 },
    strategyRewards: {
      '全力维系': { commitmentChange: 5, energyCost: 30, stabilityChange: 3 },
      '理性沟通': { commitmentChange: 8, energyCost: 15, stabilityChange: 5 },
      '冷处理': { commitmentChange: -15, energyCost: -15, stabilityChange: -8, risk: '猜疑加深' },
      '深度坦白': { commitmentChange: 20, energyCost: 20, stabilityChange: 5, risk: '谎言被揭穿则承诺-20' },
    },
  },
  {
    id: 'trial_trst_02',
    name: '异性朋友',
    category: '信任',
    description: '对方和一位异性朋友走得很近，引发不安',
    hint: '建立边界比追问更有效',
    duration: 1,
    autoDamage: { commitment: -5, energy: -5, stability: -8 },
    strategyRewards: {
      '全力维系': { commitmentChange: 5, energyCost: 30, stabilityChange: 5 },
      '理性沟通': { commitmentChange: 5, energyCost: 15, stabilityChange: 12 },
      '暂时退让': { commitmentChange: -3, energyCost: 5, stabilityChange: 0 },
      '冷处理': { commitmentChange: -8, energyCost: -15, stabilityChange: -5 },
    },
  },
  {
    id: 'trial_trst_03',
    name: '隐瞒发现',
    category: '信任',
    description: '发现对方隐瞒了一件重要的事情',
    hint: '信任一旦崩塌很难重建',
    duration: 1,
    autoDamage: { commitment: -15, energy: -10, stability: -20 },
    strategyRewards: {
      '全力维系': { commitmentChange: 5, energyCost: 30, stabilityChange: 3 },
      '理性沟通': { commitmentChange: 8, energyCost: 15, stabilityChange: 5 },
      '冷处理': { commitmentChange: -10, energyCost: -15, stabilityChange: -10, risk: '关系可能直接崩盘' },
      '深度坦白': { commitmentChange: 10, energyCost: 20, stabilityChange: 3, risk: '可能引发更大的信任危机' },
    },
  },
  {
    id: 'trial_trst_04',
    name: '多线操作',
    category: '信任',
    description: '发现对方同时在和其他人暧昧',
    hint: '几乎无法挽救的信任危机',
    duration: 1,
    autoDamage: { commitment: -20, energy: -15, stability: -25 },
    strategyRewards: {
      '全力维系': { commitmentChange: 3, energyCost: 30, stabilityChange: 0, risk: '可能被当成舔狗' },
      '理性沟通': { commitmentChange: 5, energyCost: 15, stabilityChange: 2 },
      '冷处理': { commitmentChange: -15, energyCost: -15, stabilityChange: -10 },
      '深度坦白': { commitmentChange: 8, energyCost: 20, stabilityChange: 0, risk: '对方可能不领情' },
    },
  },
];

// ============================================
// 社交考验（3张）
// ============================================
const SOCIAL_TRIALS: TrialCard[] = [
  {
    id: 'trial_socl_01',
    name: '朋友反对',
    category: '社交',
    description: '好朋友明确表示不看好这段关系',
    hint: '外部压力考验内心坚定程度',
    duration: 1,
    autoDamage: { commitment: -5, energy: -8, stability: -5 },
    strategyRewards: {
      '全力维系': { commitmentChange: 8, energyCost: 30, stabilityChange: 5 },
      '理性沟通': { commitmentChange: 5, energyCost: 15, stabilityChange: 5 },
      '暂时退让': { commitmentChange: -5, energyCost: 5, stabilityChange: -2 },
      '冷处理': { commitmentChange: -3, energyCost: -15, stabilityChange: 0 },
      '朋友助攻': { commitmentChange: 10, energyCost: 10, stabilityChange: 10 },
    },
  },
  {
    id: 'trial_socl_02',
    name: '家人质疑',
    category: '社交',
    description: '家人对这段关系提出质疑和反对',
    hint: '家庭压力是中国式恋爱的经典考验',
    duration: 2,
    autoDamage: { commitment: -8, energy: -10, stability: -8 },
    strategyRewards: {
      '全力维系': { commitmentChange: 10, energyCost: 30, stabilityChange: 5 },
      '理性沟通': { commitmentChange: 8, energyCost: 15, stabilityChange: 8 },
      '暂时退让': { commitmentChange: -5, energyCost: 5, stabilityChange: 0 },
      '冷处理': { commitmentChange: -8, energyCost: -15, stabilityChange: -5 },
    },
  },
  {
    id: 'trial_socl_03',
    name: '公开压力',
    category: '社交',
    description: '被迫在社交场合公开关系状态，面临各方审视',
    hint: '坦然面对比逃避更好',
    duration: 1,
    autoDamage: { commitment: -3, energy: -8, stability: -5 },
    strategyRewards: {
      '全力维系': { commitmentChange: 10, energyCost: 30, stabilityChange: 8 },
      '理性沟通': { commitmentChange: 5, energyCost: 15, stabilityChange: 5 },
      '暂时退让': { commitmentChange: -5, energyCost: 5, stabilityChange: -3 },
      '冷处理': { commitmentChange: -5, energyCost: -15, stabilityChange: -2 },
    },
  },
];

// ============================================
// 价值观考验（4张）
// ============================================
const VALUES_TRIALS: TrialCard[] = [
  {
    id: 'trial_vals_01',
    name: '生育分歧',
    category: '价值观',
    description: '在要不要孩子的问题上产生根本分歧',
    hint: '这是最难调和的价值观冲突之一',
    duration: 2,
    autoDamage: { commitment: -10, energy: -8, stability: -10 },
    strategyRewards: {
      '全力维系': { commitmentChange: 5, energyCost: 30, stabilityChange: 3 },
      '理性沟通': { commitmentChange: 8, energyCost: 15, stabilityChange: 8 },
      '暂时退让': { commitmentChange: -5, energyCost: 5, stabilityChange: 0, risk: '问题只是被搁置' },
      '冷处理': { commitmentChange: -10, energyCost: -15, stabilityChange: -5 },
      '深度坦白': { commitmentChange: 15, energyCost: 20, stabilityChange: 5, risk: '可能发现无法调和' },
    },
  },
  {
    id: 'trial_vals_02',
    name: '定居争执',
    category: '价值观',
    description: '在定居城市的选择上无法达成一致',
    hint: '需要双方都做出让步',
    duration: 2,
    autoDamage: { commitment: -8, energy: -8, stability: -8 },
    strategyRewards: {
      '全力维系': { commitmentChange: 8, energyCost: 30, stabilityChange: 5 },
      '理性沟通': { commitmentChange: 8, energyCost: 15, stabilityChange: 8 },
      '暂时退让': { commitmentChange: -3, energyCost: 5, stabilityChange: 2 },
      '冷处理': { commitmentChange: -8, energyCost: -15, stabilityChange: -3 },
    },
  },
  {
    id: 'trial_vals_03',
    name: '职业发展',
    category: '价值观',
    description: '一方面临重大职业机会，但可能影响关系',
    hint: '支持对方的梦想还是维护关系稳定？',
    duration: 1,
    autoDamage: { commitment: -5, energy: -8, stability: -5 },
    strategyRewards: {
      '全力维系': { commitmentChange: 10, energyCost: 30, stabilityChange: 5 },
      '理性沟通': { commitmentChange: 8, energyCost: 15, stabilityChange: 8 },
      '暂时退让': { commitmentChange: -3, energyCost: 5, stabilityChange: 2 },
      '冷处理': { commitmentChange: -5, energyCost: -15, stabilityChange: -2 },
    },
  },
  {
    id: 'trial_vals_04',
    name: '家庭责任',
    category: '价值观',
    description: '一方家庭出现状况，需要承担更多家庭责任',
    hint: '考验对方是否愿意一起承担',
    duration: 2,
    autoDamage: { commitment: -5, energy: -12, stability: -5 },
    strategyRewards: {
      '全力维系': { commitmentChange: 12, energyCost: 30, stabilityChange: 8 },
      '理性沟通': { commitmentChange: 8, energyCost: 15, stabilityChange: 5 },
      '暂时退让': { commitmentChange: -3, energyCost: 5, stabilityChange: 0 },
      '冷处理': { commitmentChange: -8, energyCost: -15, stabilityChange: -5, risk: '对方觉得你自私' },
    },
  },
];

// ============================================
// 突发考验（3张）
// ============================================
const EMERGENCY_TRIALS: TrialCard[] = [
  {
    id: 'trial_emrg_01',
    name: '生病住院',
    category: '突发',
    description: '一方突然生病住院，需要照顾',
    hint: '患难见真情',
    duration: 1,
    autoDamage: { commitment: -3, energy: -15, stability: -3 },
    strategyRewards: {
      '全力维系': { commitmentChange: 15, energyCost: 30, stabilityChange: 10 },
      '理性沟通': { commitmentChange: 5, energyCost: 15, stabilityChange: 3 },
      '暂时退让': { commitmentChange: -5, energyCost: 5, stabilityChange: -2 },
      '冷处理': { commitmentChange: -15, energyCost: -15, stabilityChange: -8, risk: '对方彻底寒心' },
    },
  },
  {
    id: 'trial_emrg_02',
    name: '意外事故',
    category: '突发',
    description: '一场意外打乱了所有计划',
    hint: '危机中最能看清一个人',
    duration: 1,
    autoDamage: { commitment: -5, energy: -15, stability: -10 },
    strategyRewards: {
      '全力维系': { commitmentChange: 12, energyCost: 30, stabilityChange: 8 },
      '理性沟通': { commitmentChange: 8, energyCost: 15, stabilityChange: 5 },
      '暂时退让': { commitmentChange: -3, energyCost: 5, stabilityChange: 0 },
      '冷处理': { commitmentChange: -10, energyCost: -15, stabilityChange: -5 },
    },
  },
  {
    id: 'trial_emrg_03',
    name: '第三者出现',
    category: '突发',
    description: '一个有魅力的第三者出现，考验关系的坚固程度',
    hint: '信任和承诺的终极考验',
    duration: 2,
    autoDamage: { commitment: -10, energy: -10, stability: -15 },
    strategyRewards: {
      '全力维系': { commitmentChange: 12, energyCost: 30, stabilityChange: 8 },
      '理性沟通': { commitmentChange: 10, energyCost: 15, stabilityChange: 10 },
      '暂时退让': { commitmentChange: -10, energyCost: 5, stabilityChange: -8, risk: '对方可能真的动摇' },
      '冷处理': { commitmentChange: -12, energyCost: -15, stabilityChange: -10, risk: '给第三者可乘之机' },
      '深度坦白': { commitmentChange: 15, energyCost: 20, stabilityChange: 5, risk: '可能暴露自己的不安全感' },
    },
  },
];

/** 完整的第三幕考验卡牌池（20张） */
export const ACT3_TRIAL_POOL: TrialCard[] = [
  ...DISTANCE_TRIALS,
  ...ECONOMY_TRIALS,
  ...TRUST_TRIALS,
  ...SOCIAL_TRIALS,
  ...VALUES_TRIALS,
  ...EMERGENCY_TRIALS,
];

/**
 * 从考验池中随机抽取指定数量的考验卡
 * 确保类别不重复（尽量覆盖不同类型的考验）
 */
export function drawTrialCards(count: number, seed?: number): TrialCard[] {
  const rng = seed !== undefined
    ? () => { seed = (seed! * 16807) % 2147483647; return (seed - 1) / 2147483646; }
    : Math.random;

  const categories: string[] = ['距离', '经济', '信任', '社交', '价值观', '突发'];
  const shuffledCategories = categories.sort(() => rng() - 0.5);

  const result: TrialCard[] = [];

  // 优先从不同类别各抽一张
  for (const cat of shuffledCategories) {
    if (result.length >= count) break;
    const candidates = ACT3_TRIAL_POOL.filter(
      t => t.category === cat && !result.includes(t),
    );
    if (candidates.length > 0) {
      const idx = Math.floor(rng() * candidates.length);
      result.push(candidates[idx]);
    }
  }

  // 如果还不够，从剩余池中补充
  if (result.length < count) {
    const remaining = ACT3_TRIAL_POOL.filter(t => !result.includes(t));
    const shuffled = remaining.sort(() => rng() - 0.5);
    result.push(...shuffled.slice(0, count - result.length));
  }

  return result;
}
